/**
 * API Client for Amtrend Command Center
 * Handles all communication with the backend
 */

const API_BASE = '/api'

class ApiError extends Error {
  constructor(message, status, data) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

/**
 * Get the stored JWT token
 */
function getToken() {
  return localStorage.getItem('amtrend_token')
}

/**
 * Make an authenticated API request
 */
async function request(endpoint, options = {}) {
  const token = getToken()

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  })

  // Handle non-JSON responses
  const contentType = response.headers.get('content-type')
  let data
  if (contentType && contentType.includes('application/json')) {
    data = await response.json()
  } else {
    data = await response.text()
  }

  if (!response.ok) {
    throw new ApiError(
      data.detail || data.message || 'An error occurred',
      response.status,
      data
    )
  }

  return data
}

/**
 * Authentication
 */
export async function login(username, password) {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ username, password }),
  })

  const data = await response.json()

  if (!response.ok) {
    throw new ApiError(data.detail || 'Login failed', response.status, data)
  }

  // Store the token
  localStorage.setItem('amtrend_token', data.access_token)

  return data
}

export function logout() {
  localStorage.removeItem('amtrend_token')
}

export function isAuthenticated() {
  return !!getToken()
}

/**
 * Get current user info
 */
export async function getCurrentUser() {
  return request('/auth/me')
}

/**
 * Chat with AI assistant
 * @param {string} message - The user's message
 * @param {Array} history - Previous conversation history
 * @param {Array} attachments - Optional attachments [{base64, type, name, isImage, isPDF}]
 */
export async function chat(message, history = [], attachments = []) {
  return request('/chat', {
    method: 'POST',
    body: JSON.stringify({
      message,
      history,
      attachments: attachments.map(a => ({
        data: a.base64,
        media_type: a.type,
        name: a.name,
        is_image: a.isImage,
        is_pdf: a.isPDF,
        is_csv: a.isCSV,
        is_excel: a.isExcel,
      })),
    }),
  })
}

/**
 * Chat with AI assistant using streaming response
 * @param {string} message - The user's message
 * @param {Array} history - Previous conversation history
 * @param {Function} onDelta - Callback for text content deltas
 * @param {Function} onToolCall - Callback when a tool is called
 * @param {Function} onDone - Callback when streaming is complete (receives toolsUsed and timing)
 * @param {Array} attachments - Optional attachments [{base64, type, name, isImage, isPDF, isCSV, isExcel}]
 * @param {Function} onArtifact - Callback when an artifact is received (photo gallery, etc.)
 * @param {Function} onToolResult - Callback when a tool completes (receives tool name and success boolean)
 * @param {Function} onCitations - Callback when citations arrive on a tool_result (receives tool name + Citation[])
 * @returns {Promise<void>}
 */
export async function chatStream(message, history = [], onDelta, onToolCall, onDone, attachments = [], onArtifact = null, onToolResult = null, signal = null, onApprovalRequired = null, onCitations = null, conversationId = null, onGroundingAlert = null) {
  const token = getToken()
  const response = await fetch(`${API_BASE}/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      message,
      history,
      // Required by backend _persisting_stream_wrapper (chat.py) to INSERT the
      // assistant message at end-of-stream. Without this, the wrapper's guard
      // `if conversation_id and accumulated_text:` short-circuits and the
      // streamed response is never persisted.
      conversation_id: conversationId,
      attachments: attachments.map(a => ({
        data: a.base64,
        media_type: a.type,
        name: a.name,
        is_image: a.isImage,
        is_pdf: a.isPDF,
        is_csv: a.isCSV,
        is_excel: a.isExcel,
      })),
    }),
    ...(signal ? { signal } : {}),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.detail || errorData.message || 'Streaming request failed',
      response.status,
      errorData
    )
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let receivedDone = false
  let toolsUsed = []
  let timing = null

  // Client-side stall backstop. The server emits a heartbeat every ~10s during
  // long work and a watchdog error+done at ~90s, so this only fires if the
  // server goes completely silent (process death / dropped connection) — at
  // which point we stop reading rather than spin forever. 120s > server's 90s
  // so the graceful server-side error wins whenever the server is still alive.
  const IDLE_MS = 120000
  const readWithIdleTimeout = async () => {
    let timer
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(
        () => reject(new ApiError('The response stalled (no data from the server). Please try again.', 504)),
        IDLE_MS
      )
    })
    try {
      return await Promise.race([reader.read(), timeout])
    } finally {
      clearTimeout(timer)
    }
  }

  try {
    while (true) {
      const { done, value } = await readWithIdleTimeout()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')

      // Keep the last incomplete line in the buffer
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue

        const jsonStr = line.slice(6).trim()
        if (!jsonStr || jsonStr === '[DONE]') continue

        try {
          const data = JSON.parse(jsonStr)
          if (data.type === 'text_delta') {
            onDelta(data.content)
          } else if (data.type === 'tool_call' || data.type === 'tool_executing') {
            onToolCall(data.tool)
            // Track tools used in case we don't get a done event
            if (!toolsUsed.includes(data.tool)) {
              toolsUsed.push(data.tool)
            }
          } else if (data.type === 'tool_result') {
            // Tool completed - track it and notify
            if (data.tool && !toolsUsed.includes(data.tool)) {
              toolsUsed.push(data.tool)
            }
            if (onToolResult && data.tool) {
              onToolResult(data.tool, data.success !== false)
            }
            // PR1: forward citations to the right-pane SourcePane.
            // Validation happens in lib/citations.js (zod) — we just hand the
            // raw array off and the caller validates before storing.
            if (onCitations && Array.isArray(data.citations) && data.citations.length > 0) {
              onCitations(data.tool, data.citations)
            }
          } else if (data.type === 'tool_approval_required') {
            // Write tool needs user approval before execution.
            // PR4: data.action_card is optional; when present the frontend
            // routes through ActionCardView in the right pane instead of
            // the legacy WriteApprovalModal.
            if (onApprovalRequired) {
              onApprovalRequired(
                data.approval_id, data.tool, data.input, data.action_card,
                // Server-sent deadline. Passed through rather than recomputed
                // client-side so the UI can never disagree with the gate about
                // when the approval stops working.
                data.expires_at || null,
              )
            }
          } else if (data.type === 'tool_approved') {
            // Tool was approved and is now executing
            if (onToolCall) onToolCall(data.tool)
          } else if (data.type === 'tool_denied') {
            // Tool was denied by user
            if (onToolResult) onToolResult(data.tool, false)
          } else if (data.type === 'artifact') {
            // Artifact received (photo gallery, etc.) - display in artifact panel
            if (onArtifact && data.artifact) {
              onArtifact(data.artifact)
            }
          } else if (data.type === 'grounding_alert') {
            if (onGroundingAlert) onGroundingAlert(data.violations || [], data.bad_urls || [])
          } else if (data.type === 'done') {
            receivedDone = true
            toolsUsed = data.tools_used || toolsUsed
            timing = data.timing || null
            onDone(toolsUsed, timing)
          } else if (data.type === 'error') {
            throw new ApiError(data.message || 'Streaming error', 500, data)
          }
        } catch (parseErr) {
          // Skip malformed JSON lines
          if (parseErr instanceof ApiError) throw parseErr
          console.warn('Failed to parse SSE data:', jsonStr)
        }
      }
    }

    // Process any remaining data in buffer
    if (buffer.startsWith('data: ')) {
      const jsonStr = buffer.slice(6).trim()
      if (jsonStr && jsonStr !== '[DONE]') {
        try {
          const data = JSON.parse(jsonStr)
          if (data.type === 'done') {
            receivedDone = true
            onDone(data.tools_used || toolsUsed, data.timing || null)
          }
        } catch (e) {
          // Ignore parse errors on final buffer
        }
      }
    }

    // If stream ended without a done event, call onDone anyway
    // This handles cases where the connection closes gracefully but without explicit done
    if (!receivedDone) {
      console.warn('Stream ended without done event, calling onDone with tracked tools')
      onDone(toolsUsed, timing)
    }
  } finally {
    reader.releaseLock()
  }
}

/**
 * Approve or deny a pending write-tool execution
 */
export async function approveToolExecution(approvalId, approved) {
  return request(`/chat/approve/${approvalId}`, {
    method: 'POST',
    body: JSON.stringify({ approved }),
  })
}

/**
 * Fetch the action audit log. Optional filters: kind, status, username.
 *
 * @param {{ kind?: string, status?: string, username?: string, limit?: number, offset?: number }} params
 */
export async function getActionAudit(params = {}) {
  const q = new URLSearchParams()
  if (params.kind)     q.set('kind', params.kind)
  if (params.status)   q.set('status', params.status)
  if (params.username) q.set('username', params.username)
  q.set('limit', String(params.limit ?? 100))
  if (params.offset)   q.set('offset', String(params.offset))
  return request(`/actions/audit?${q.toString()}`)
}

/**
 * Approve or deny a pending Action Card.
 * Server-validates edited_params; returns 422 if edits violate the schema,
 * 428 if the action is high-value and `confirmed_high_value` wasn't set.
 *
 * @param {string} approvalId
 * @param {{ approved: boolean, edited_params?: object, confirmed_high_value?: boolean }} body
 */
export async function approveAction(approvalId, body) {
  return request(`/chat/approve/${approvalId}`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Execute a tool directly
 */
export async function executeTool(name, params = {}) {
  return request('/tools/execute', {
    method: 'POST',
    body: JSON.stringify({
      tool_name: name,
      parameters: params,
    }),
  })
}

/**
 * Get available tools
 */
export async function getTools() {
  return request('/tools')
}

// ==============================================
// Sessions API
// ==============================================

/**
 * Get all sessions for the current user
 */
export async function getSessions(limit = 50) {
  return request(`/sessions?limit=${limit}`)
}

/**
 * Create a new session
 */
export async function createSession(title = 'New conversation') {
  return request('/sessions', {
    method: 'POST',
    body: JSON.stringify({ title }),
  })
}

/**
 * Get a session with all its messages
 */
export async function getSession(sessionId) {
  return request(`/sessions/${sessionId}`)
}

/**
 * Update a session's title
 */
export async function updateSession(sessionId, title) {
  return request(`/sessions/${sessionId}`, {
    method: 'PATCH',
    body: JSON.stringify({ title }),
  })
}

/**
 * Delete a session
 */
export async function deleteSessionApi(sessionId) {
  return request(`/sessions/${sessionId}`, {
    method: 'DELETE',
  })
}

/**
 * Add a message to a session
 * @param {Array} [citations] - PR2: right-pane source citations attached to the message
 */
export async function addMessageApi(sessionId, role, content, toolsUsed = null, citations = null) {
  return request(`/sessions/${sessionId}/messages`, {
    method: 'POST',
    body: JSON.stringify({
      role,
      content,
      tools_used: toolsUsed,
      citations,
    }),
  })
}

/**
 * Get messages for a session
 */
export async function getSessionMessages(sessionId, limit = 100) {
  return request(`/sessions/${sessionId}/messages?limit=${limit}`)
}

// ==============================================
// Knowledge Base — Data Sources (PR3)
// ==============================================

/** Fetch the full source tree (Epicor / M365 / Plaid / Training Corpus). */
export async function getKnowledgeSources({ refresh = false } = {}) {
  return request(`/knowledge/sources${refresh ? '?refresh=true' : ''}`)
}

/** Toggle whether a source is excluded from grounding. */
export async function setSourceExclusion(sourceId, excluded) {
  return request(`/knowledge/sources/${encodeURIComponent(sourceId)}/exclude`, {
    method: 'POST',
    body: JSON.stringify({ excluded }),
  })
}

/** Acknowledge a re-ingest request (PR3 stub; real job system in PR4). */
export async function reingestSource(sourceId) {
  return request(`/knowledge/sources/${encodeURIComponent(sourceId)}/reingest`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

/** Recent tool calls for this source (telemetry-derived ingestion log). */
export async function getIngestLog(sourceId, limit = 50) {
  return request(`/knowledge/ingest-log/${encodeURIComponent(sourceId)}?limit=${limit}`)
}

/** Audit log — PR4 owns the real implementation. */
export async function getKnowledgeAudit() {
  return request(`/knowledge/audit`)
}

// ==============================================
// Convenience methods for common tool calls
// ==============================================

/**
 * Cash & Finance
 */
export async function getDailyCashPosition() {
  return executeTool('daily_cash_position', {})
}

export async function getARSummary(customerId = null) {
  return executeTool('get_ar_aging', { customer_id: customerId })
}

export async function getAPSummary() {
  return executeTool('get_ap_aging', {})
}

export async function getCustomerARBalance(customerId) {
  return executeTool('get_customer_ar_balance', { customer_id: customerId })
}

export async function getVendorAPBalance(vendorId) {
  return executeTool('get_vendor_ap_balance', { vendor_num: vendorId })
}

export async function checkARAlerts() {
  return executeTool('check_ar_alerts', {})
}

export async function checkAPAlerts() {
  return executeTool('check_ap_alerts', {})
}

/**
 * Jobs & Production
 */
export async function searchJobs(filters = {}) {
  return executeTool('search_jobs', filters)
}

export async function getJob(jobNum) {
  return executeTool('get_job', { job_num: jobNum })
}

export async function getJobCosts(jobNum) {
  return executeTool('get_job_costs', { job_num: jobNum })
}

export async function getJobSchedule(jobNum) {
  return executeTool('get_job_schedule', { job_num: jobNum })
}

export async function checkJobOverruns() {
  return executeTool('check_job_overruns', {})
}

export async function checkLateJobs() {
  return executeTool('check_late_jobs', {})
}

export async function getProductionCapacity() {
  return executeTool('production_capacity', {})
}

/**
 * Quotes
 */
export async function searchQuotes(filters = {}) {
  return executeTool('search_quotes', filters)
}

export async function getQuote(quoteNum) {
  return executeTool('get_quote', { quote_num: quoteNum })
}

export async function getQuoteLines(quoteNum) {
  return executeTool('get_quote_lines', { quote_num: quoteNum })
}

export async function checkExpiringQuotes(daysAhead = 30) {
  return executeTool('check_expiring_quotes', { days_ahead: daysAhead })
}

export async function getQuoteBenchmark() {
  return executeTool('quote_benchmark', {})
}

/**
 * Orders
 */
export async function searchOrders(filters = {}) {
  return executeTool('search_orders', filters)
}

export async function getOrder(orderNum) {
  return executeTool('get_order', { order_num: orderNum })
}

export async function getOrderBalance(orderNum) {
  return executeTool('get_order_balance', { order_num: orderNum })
}

export async function getOrderReleases(orderNum) {
  return executeTool('get_order_releases', { order_num: orderNum })
}

export async function getOrdersByShipDate(startDate, endDate) {
  return executeTool('get_orders_by_ship_date', {
    start_date: startDate,
    end_date: endDate,
  })
}

export async function getSalesBacklog() {
  return executeTool('sales_backlog', {})
}

/**
 * Invoices
 */
export async function searchInvoices(filters = {}) {
  return executeTool('search_invoices', filters)
}

export async function getInvoice(invoiceNum) {
  return executeTool('get_invoice', { invoice_num: invoiceNum })
}

export async function getInvoiceLines(invoiceNum) {
  return executeTool('get_invoice_lines', { invoice_num: invoiceNum })
}

/**
 * Customers
 */
export async function searchCustomers(filters = {}) {
  return executeTool('search_customers', filters)
}

export async function getCustomer(custNum) {
  return executeTool('get_customer', { cust_num: custNum })
}

export async function getCustomerContacts(custNum) {
  return executeTool('get_customer_contacts', { cust_num: custNum })
}

export async function getCustomerShipTos(custNum) {
  return executeTool('get_customer_ship_tos', { cust_num: custNum })
}

export async function getCustomerOrders(custNum) {
  return executeTool('get_customer_orders', { cust_num: custNum })
}

export async function getCustomerPriceList(custNum) {
  return executeTool('get_customer_price_list', { cust_num: custNum })
}

export async function getCustomerARSummary(custNum) {
  return executeTool('get_customer_ar_summary', { cust_num: custNum })
}

export async function getCustomerCreditStatus(custNum) {
  return executeTool('get_customer_credit_status', { cust_num: custNum })
}

export async function getCustomerProfitability() {
  return executeTool('customer_profitability', {})
}

export async function checkCreditHolds() {
  return executeTool('check_credit_holds', {})
}

/**
 * Vendors & AP
 */
export async function searchVendors(filters = {}) {
  return executeTool('search_vendors', filters)
}

export async function getVendor(vendorNum) {
  return executeTool('get_vendor', { vendor_num: vendorNum })
}

export async function searchAPInvoices(filters = {}) {
  return executeTool('search_ap_invoices', filters)
}

export async function getAPInvoice(vendorNum, invoiceNum) {
  return executeTool('get_ap_invoice', {
    vendor_num: vendorNum,
    invoice_num: invoiceNum,
  })
}

export async function getAPInvoicesDue(daysAhead = 30) {
  return executeTool('get_ap_invoices_due', { days_ahead: daysAhead })
}

/**
 * Parts & Inventory
 */
export async function searchParts(filters = {}) {
  return executeTool('search_parts', filters)
}

export async function getPart(partNum) {
  return executeTool('get_part', { part_num: partNum })
}

export async function getPartInventory(partNum) {
  return executeTool('get_part_inventory', { part_num: partNum })
}

export async function getPartCosts(partNum) {
  return executeTool('get_part_costs', { part_num: partNum })
}

export async function getPartWhereUsed(partNum) {
  return executeTool('get_part_where_used', { part_num: partNum })
}

export async function getPartTransactions(partNum, days = 30) {
  return executeTool('get_part_transactions', {
    part_num: partNum,
    days: days,
  })
}

/**
 * Purchase Orders
 */
export async function searchPOs(filters = {}) {
  return executeTool('search_pos', filters)
}

export async function getPO(poNum) {
  return executeTool('get_po', { po_num: poNum })
}

export async function getPOReleases(poNum) {
  return executeTool('get_po_releases', { po_num: poNum })
}

export async function getReceipts(poNum) {
  return executeTool('get_receipts', { po_num: poNum })
}

/**
 * Shipments
 */
export async function searchShipments(filters = {}) {
  return executeTool('search_shipments', filters)
}

export async function getShipment(packNum) {
  return executeTool('get_shipment', { pack_num: packNum })
}

/**
 * Analytics & Reports
 */
export async function getMarginByJob() {
  return executeTool('margin_by_job', {})
}

// Export all as default object too for convenience
export default {
  login,
  logout,
  isAuthenticated,
  getCurrentUser,
  chat,
  chatStream,
  executeTool,
  getTools,
  getDailyCashPosition,
  getARSummary,
  getAPSummary,
  getCustomerARBalance,
  getVendorAPBalance,
  checkARAlerts,
  checkAPAlerts,
  searchJobs,
  getJob,
  getJobCosts,
  getJobSchedule,
  checkJobOverruns,
  checkLateJobs,
  getProductionCapacity,
  searchQuotes,
  getQuote,
  getQuoteLines,
  checkExpiringQuotes,
  getQuoteBenchmark,
  searchOrders,
  getOrder,
  getOrderBalance,
  getOrderReleases,
  getOrdersByShipDate,
  getSalesBacklog,
  searchInvoices,
  getInvoice,
  getInvoiceLines,
  searchCustomers,
  getCustomer,
  getCustomerContacts,
  getCustomerShipTos,
  getCustomerOrders,
  getCustomerPriceList,
  getCustomerARSummary,
  getCustomerCreditStatus,
  getCustomerProfitability,
  checkCreditHolds,
  searchVendors,
  getVendor,
  searchAPInvoices,
  getAPInvoice,
  getAPInvoicesDue,
  searchParts,
  getPart,
  getPartInventory,
  getPartCosts,
  getPartWhereUsed,
  getPartTransactions,
  searchPOs,
  getPO,
  getPOReleases,
  getReceipts,
  searchShipments,
  getShipment,
  getMarginByJob,
}

/**
 * Production Flow / Runway Monitor
 * Reads the amtrend-production-flow snapshot project via the backend proxy.
 */
export async function getRunwaySummary() {
  return request('/production/summary')
}

export async function getRunwayList({ limit = 150, includeOverdue = true } = {}) {
  return request(`/production/runway?limit=${limit}&include_overdue=${includeOverdue}`)
}

export async function getRunwayOrders({ limit = 400, includeOverdue = true } = {}) {
  return request(`/production/runway-orders?limit=${limit}&include_overdue=${includeOverdue}`)
}

export async function getRunwayExceptions(queue, limit = 300) {
  return request(`/production/exceptions/${queue}?limit=${limit}`)
}

export async function getRunwayLateCurve() {
  return request('/production/validation/late-curve')
}

export async function getRiskBands() {
  return request('/production/risk-bands')
}

export async function getModeRisk() {
  return request('/production/mode-risk')
}
